<!-- EstatusWhatsapp.vue — Estado real de un mensaje enviado por WhatsApp -->
<!-- Lo que muestra viene del webhook de Meta, no de nuestra petición de envío. -->
<template>
  <div v-if="mensaje" class="wa-estatus" :class="p.clase">
    <!-- Línea principal: badge + hora del último cambio -->
    <div class="wa-linea">
      <span class="wa-badge" :class="p.clase" :title="p.ayuda">
        <span v-if="cargandoEstado" class="spinner-border spinner-border-sm wa-spinner"></span>
        <i v-else class="bi" :class="p.icono"></i>
        {{ p.label }}
      </span>

      <span v-if="horaUltimoCambio" class="wa-hora">{{ horaUltimoCambio }}</span>

      <span v-if="mensaje.enviado_por_nombre" class="wa-autor">
        · por {{ mensaje.enviado_por_nombre }}
      </span>
    </div>

    <!-- Progreso: enviado → entregado → leído -->
    <div v-if="!hayFallo" class="wa-pasos">
      <span
        v-for="paso in pasos"
        :key="paso.clave"
        class="wa-paso"
        :class="{ cumplido: paso.fecha }"
        :title="paso.fecha ? `${paso.label}: ${formatFechaHora(paso.fecha)}` : paso.pendienteAyuda"
      >
        <i class="bi" :class="paso.fecha ? 'bi-check-circle-fill' : 'bi-circle'"></i>
        {{ paso.label }}
      </span>
    </div>

    <!-- Motivo del fallo -->
    <div v-else class="wa-error">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      <div>
        <strong>{{ p.ayuda }}</strong>
        <span v-if="detalleError" class="wa-error-detalle">{{ detalleError }}</span>
        <span v-if="mensaje.error_codigo" class="wa-error-codigo">
          Código {{ mensaje.error_codigo }}
        </span>
      </div>
    </div>

    <!-- Aviso mientras Meta no confirma nada -->
    <p v-if="esperandoConfirmacion" class="wa-aviso">
      <i class="bi bi-info-circle me-1"></i>
      WhatsApp aún no confirma la entrega. El estado se actualiza solo en cuanto
      lo reporte, aunque cierres esta ventana.
    </p>
  </div>
</template>

<script setup>
import { computed } from "vue";
import {
  presentacion,
  enTransito,
  fallo,
  textoError,
  formatFechaHora,
} from "./composables/useMensajesWhatsapp.js";

const props = defineProps({
  mensaje: { type: Object, default: null },
});

const p = computed(() => presentacion(props.mensaje?.estatus));
const hayFallo = computed(() => fallo(props.mensaje));
const cargandoEstado = computed(() => enTransito(props.mensaje));
const detalleError = computed(() => textoError(props.mensaje));

// Solo se avisa cuando el mensaje ya salió pero Meta no ha reportado nada.
const esperandoConfirmacion = computed(
  () => props.mensaje?.estatus === "ACEPTADO"
);

const horaUltimoCambio = computed(() => {
  const m = props.mensaje;
  if (!m) return "";
  const fecha =
    m.fecha_fallido ||
    m.fecha_leido ||
    m.fecha_entregado ||
    m.fecha_enviado ||
    m.fecha_creacion;
  return formatFechaHora(fecha);
});

const pasos = computed(() => {
  const m = props.mensaje || {};
  return [
    {
      clave: "enviado",
      label: "Enviado",
      fecha: m.fecha_enviado,
      pendienteAyuda: "WhatsApp todavía no lo despacha.",
    },
    {
      clave: "entregado",
      label: "Entregado",
      fecha: m.fecha_entregado,
      pendienteAyuda: "Aún no llega al teléfono del cliente.",
    },
    {
      clave: "leido",
      label: "Leído",
      fecha: m.fecha_leido,
      pendienteAyuda: "El cliente todavía no lo abre.",
    },
  ];
});
</script>

<style scoped>
.wa-estatus {
  border-radius: 8px;
  padding: 8px 10px;
  background: rgba(96, 108, 56, 0.05);
  border: 1px solid rgba(96, 108, 56, 0.18);
  margin-top: 8px;
}
.wa-estatus.wa-fallido {
  background: rgba(198, 40, 40, 0.06);
  border-color: rgba(198, 40, 40, 0.28);
}
.wa-estatus.wa-leido,
.wa-estatus.wa-entregado {
  background: rgba(37, 211, 102, 0.08);
  border-color: rgba(37, 211, 102, 0.3);
}

/* ── Línea principal ─────────────────────────────────────── */
.wa-linea {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.wa-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 20px;
  padding: 2px 10px;
  background: white;
  border: 1.5px solid currentColor;
}
.wa-spinner {
  width: 0.7rem;
  height: 0.7rem;
  border-width: 2px;
}
.wa-hora,
.wa-autor {
  font-size: 0.72rem;
  color: rgba(64, 34, 8, 0.55);
}

/* Colores por estado */
.wa-badge.wa-pendiente,
.wa-badge.wa-aceptado {
  color: #6d4c41;
}
.wa-badge.wa-enviado {
  color: #1565c0;
}
.wa-badge.wa-entregado {
  color: #2e7d32;
}
.wa-badge.wa-leido {
  color: #1b5e20;
}
.wa-badge.wa-fallido {
  color: #c62828;
}

/* ── Pasos ───────────────────────────────────────────────── */
.wa-pasos {
  display: flex;
  gap: 14px;
  margin-top: 6px;
  flex-wrap: wrap;
}
.wa-paso {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(64, 34, 8, 0.35);
  cursor: default;
}
.wa-paso.cumplido {
  color: #2e7d32;
}

/* ── Error ───────────────────────────────────────────────── */
.wa-error {
  display: flex;
  align-items: flex-start;
  margin-top: 6px;
  font-size: 0.78rem;
  color: #c62828;
}
.wa-error-detalle,
.wa-error-codigo {
  display: block;
  font-weight: 400;
  font-size: 0.75rem;
  margin-top: 2px;
}
.wa-error-codigo {
  opacity: 0.7;
}

/* ── Aviso ───────────────────────────────────────────────── */
.wa-aviso {
  font-size: 0.72rem;
  color: rgba(64, 34, 8, 0.5);
  margin: 6px 0 0;
}
</style>
